import { getChildren, getInheritedDeadline, getInheritedTimeline } from './taskHierarchyUtils';
import { getStatusDisplayMeta, taskIsOffTrack } from './taskSchedulingUtils';
import {
    buildBadgeStyle,
    defaultCellStyle,
    PDF_TABLE_THEME,
    resolvePdfColor,
} from './pdfTableTheme';

export { applyPdfCellStyle, buildBadgeStyle, defaultCellStyle, PDF_TABLE_THEME, resolvePdfColor } from './pdfTableTheme';

// --- Task styles ---
const TASK_PRIORITY_STYLES = {
    HIGH: { bg: '#2B35AF', color: '#FFFFFF' },
    MEDIUM: { bg: '#4361EE', color: '#FFFFFF' },
    LOW: { bg: '#4895EF', color: '#FFFFFF' },
    '': { bg: '#FFFFFF', color: '#989BAD' },
    NONE: { bg: '#FFFFFF', color: '#989BAD' },
};

export const TASK_BADGE_COLUMNS = new Set(['status', 'priority', 'deadline']);

const taskHasChildren = (task, allTasks) => getChildren(task?.id, allTasks).length > 0;

const getDeadlineValue = (task, allTasks) => {
    if (taskHasChildren(task, allTasks)) {
        return task.deadline || getInheritedDeadline(task, allTasks).deadline;
    }
    return task.deadline;
};

export const getTaskPdfCellStyle = (columnKey, task, { allTasks, selectedProject }) => {
    switch (columnKey) {
        case 'task': {
            if (taskHasChildren(task, allTasks)) {
                return {
                    textColor: PDF_TABLE_THEME.mutedTextColor,
                    halign: 'left',
                    valign: 'middle',
                    fontStyle: 'normal',
                };
            }
            return defaultCellStyle();
        }
        case 'status': {
            // Parents: muted grey label only (no inherited colored badge).
            if (taskHasChildren(task, allTasks)) {
                return {
                    textColor: PDF_TABLE_THEME.mutedTextColor,
                    fillColor: [255, 255, 255],
                    halign: 'center',
                    valign: 'middle',
                    fontStyle: 'normal',
                    badge: false,
                };
            }
            const meta = getStatusDisplayMeta(task.status, selectedProject);
            return buildBadgeStyle({ bg: meta?.bg, color: meta?.color });
        }
        case 'priority': {
            const meta = TASK_PRIORITY_STYLES[task.priority] || TASK_PRIORITY_STYLES[''];
            return buildBadgeStyle({ bg: meta.bg, color: meta.color });
        }
        case 'deadline': {
            const deadlineValue = getDeadlineValue(task, allTasks);
            if (!deadlineValue || task.status === 'CANCELLED') {
                return {
                    textColor: task.status === 'CANCELLED' ? PDF_TABLE_THEME.mutedTextColor : PDF_TABLE_THEME.textColor,
                };
            }
            const isCompleted = task.status === 'COMPLETED' || task.status === 'COMPLETE';
            const offTrack = taskIsOffTrack({
                ...task,
                deadline: deadlineValue,
                end_date: task.end_date || getInheritedTimeline(task, allTasks)?.end_date,
            });
            if (offTrack && !isCompleted) {
                return buildBadgeStyle({ bg: '#B00020', color: '#FFFFFF', badge: true });
            }
            const deadline = new Date(deadlineValue);
            const today = new Date();
            deadline.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const isOverdue = today > deadline;
            if (isOverdue && isCompleted) {
                return buildBadgeStyle({ bg: '#E8F5EE', color: '#198754', badge: true });
            }
            if (isOverdue && !isCompleted) {
                return buildBadgeStyle({ bg: '#FFEBEE', color: '#B00020', badge: true });
            }
            return defaultCellStyle();
        }
        default:
            return defaultCellStyle();
    }
};

// --- Action Log styles ---
const ACTION_LOG_STATUS = {
    NOT_STARTED: { bg: '#C0C6D8', color: 'white' },
    WORKING_ON_IT: { bg: 'var(--RAG-Amber)', color: 'white' },
    COMPLETED: { bg: 'var(--RAG-Green)', color: 'white' },
    STUCK: { bg: 'var(--RAG-Red)', color: 'white' },
    CANCELLED: { bg: '#989BAD', color: 'white' },
};

export const ACTION_LOG_BADGE_COLUMNS = new Set(['status', 'dueDate']);

export const getActionLogPdfCellStyle = (columnKey, row) => {
    if (columnKey === 'status') {
        const meta = ACTION_LOG_STATUS[row.status] || { bg: '#C0C6D8', color: '#989BAD' };
        return buildBadgeStyle({ bg: meta.bg, color: meta.color });
    }
    if (columnKey === 'dueDate' && row.due_date) {
        const due = new Date(row.due_date);
        const today = new Date();
        due.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        const isCompleted = row.status === 'COMPLETED';
        const isOverdue = today > due;
        if (isOverdue && isCompleted) {
            return buildBadgeStyle({ bg: '#E8F5EE', color: '#198754' });
        }
        if (isOverdue && !isCompleted) {
            return buildBadgeStyle({ bg: '#FFEBEE', color: '#B00020' });
        }
    }
    return defaultCellStyle();
};

// --- RAID styles ---
const RAID_STATUS = {
    NOT_STARTED: { bg: '#C0C6D8', color: 'white' },
    WORKING_ON_IT: { bg: 'var(--RAG-Amber)', color: 'white' },
    COMPLETED: { bg: 'var(--RAG-Green)', color: 'white' },
    STUCK: { bg: 'var(--RAG-Red)', color: 'white' },
    CANCELLED: { bg: '#989BAD', color: 'white' },
};

const RAID_RAG = {
    RED: { bg: 'var(--RAG-Red)', color: 'white' },
    AMBER: { bg: 'var(--RAG-Amber)', color: 'white' },
    GREEN: { bg: 'var(--RAG-Green)', color: 'white' },
};

const RAID_CATEGORY = {
    RISK: { bg: '#C0392B', color: 'white' },
    ASSUMPTION: { bg: '#7D3C98', color: 'white' },
    ISSUE: { bg: '#2874A6', color: 'white' },
    DEPENDENCY: { bg: '#1E8449', color: 'white' },
};

const RAID_LEVEL = {
    NEGLIGIBLE: { bg: '#7F8C8D', color: 'white' },
    LOW: { bg: '#27AE60', color: 'white' },
    MODERATE: { bg: '#F1C40F', color: 'white' },
    HIGH: { bg: '#E67E22', color: 'white' },
    CRITICAL: { bg: '#C0392B', color: 'white' },
};

const RAID_PROBABILITY = {
    HIGH: { bg: '#2B35AF', color: 'white' },
    MEDIUM: { bg: '#4361EE', color: 'white' },
    LOW: { bg: '#4895EF', color: 'white' },
};

export const RAID_BADGE_COLUMNS = new Set(['status', 'rag', 'category', 'level', 'probability']);

export const getRaidPdfCellStyle = (columnKey, row) => {
    const maps = {
        status: RAID_STATUS[row.status],
        rag: RAID_RAG[row.rag],
        category: RAID_CATEGORY[row.raid_category],
        level: RAID_LEVEL[row.level],
        probability: RAID_PROBABILITY[row.probability],
    };
    const meta = maps[columnKey];
    if (meta) return buildBadgeStyle({ bg: meta.bg, color: meta.color });
    if (['status', 'rag', 'category', 'level', 'probability'].includes(columnKey)) {
        return buildBadgeStyle({ bg: '#C0C6D8', color: '#989BAD' });
    }
    return defaultCellStyle();
};

// --- Lessons styles ---
export const getLessonsPdfCellStyle = (columnKey, row, { audienceOptions = [] }) => {
    if (columnKey !== 'audience') return defaultCellStyle();
    const match =
        audienceOptions.find(o => o.value === row.audience) ||
        audienceOptions.find(o => o.label === row.audience) ||
        null;
    if (match?.color) {
        return buildBadgeStyle({ bg: match.color, color: 'white' });
    }
    return buildBadgeStyle({ bg: '#C0C6D8', color: '#989BAD' });
};

export const LESSONS_BADGE_COLUMNS = new Set(['audience']);

// --- IRL styles ---
export const getIrlPdfCellStyle = (columnKey, row) => {
    if (columnKey === 'status') {
        const responded = Boolean(row?.irresponse);
        return buildBadgeStyle({
            bg: responded ? 'var(--RAG-Green)' : '#FFEBEE',
            color: responded ? 'white' : '#B00020',
        });
    }
    if (columnKey === 'priority') {
        const priority = String(row?.priorities || '').toUpperCase();
        const colors = { HIGH: '#ff0000', MEDIUM: '#ffbb33', LOW: '#33b5e5' };
        const color = colors[priority];
        if (color) {
            return { textColor: resolvePdfColor(color), fontStyle: 'bold', halign: 'left', valign: 'middle' };
        }
    }
    return defaultCellStyle();
};

export const IRL_BADGE_COLUMNS = new Set(['status']);

// --- Observations styles ---
const OBS_RAG = {
    RED: { bg: '#da2a2a', color: 'white' },
    AMBER: { bg: '#fc9c24', color: 'white' },
    GREEN: { bg: '#006400', color: 'white' },
};

const OBS_SWOT_COLORS = {
    S: '#006400',
    W: '#da2a2a',
    O: '#33b5e5',
    T: '#fc9c24',
};

export const getObservationsPdfCellStyle = (columnKey, row) => {
    if (columnKey === 'rag') {
        const meta = OBS_RAG[row.assessment];
        if (meta) return buildBadgeStyle({ bg: meta.bg, color: meta.color });
    }
    if (columnKey === 'swot' && row.swot) {
        const color = OBS_SWOT_COLORS[row.swot];
        if (color) {
            return { textColor: resolvePdfColor(color), fontStyle: 'bold', halign: 'left', valign: 'middle' };
        }
    }
    return defaultCellStyle();
};

export const OBSERVATIONS_BADGE_COLUMNS = new Set(['rag']);

// Back-compat for tasks
export const getPdfCellStyle = getTaskPdfCellStyle;
